/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: AnimatedBanner.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  09.02.23 1:14 pm.
 * Last modified: 09.02.23 1:14 pm.
 *
 * More information at: https://egt-digital.com/
 */
import React, { useEffect, useRef } from 'react';
import { fromEvent, Subscription } from 'rxjs';
import eventStore from '../eventStore';

enum VideoPlayerStates {
    HAVE_NOTHING = 0,
    HAVE_METADATA = 1,
    HAVE_CURRENT_DATA = 2,
    HAVE_FUTURE_DATA = 3,
    HAVE_ENOUGH_DATA = 4,
}

/** check whether x and  y are inside viewport area */
const isInsideVisibleArea = ({
    x,
    y,
    width,
    height,
}: {
    x: number;
    y: number;
    width: number;
    height: number;
}): boolean =>
    (x > 0 && y > 0 && x < window.innerWidth && y < window.innerHeight) ||
    (x + width > 0 && y + height > 0 && x + width < window.innerWidth && y + height < window.innerHeight);

const AnimatedBanner = ({ src }: IAnimatedBannerProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const timerRef = useRef<any>(null);

    const resetPlayerTimeout = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        const timeout = 10000 + Math.ceil(Math.random() * 10000);

        timerRef.current = setTimeout(() => {
            if (
                eventStore.SHOW.value &&
                videoRef.current?.readyState === VideoPlayerStates.HAVE_ENOUGH_DATA &&
                // performance optimization to play the vide
                videoRef.current.parentElement &&
                isInsideVisibleArea(videoRef.current.parentElement.getBoundingClientRect())
            ) {
                videoRef.current.style.display = 'unset';
                videoRef.current.play();
            } else {
                resetPlayerTimeout();
            }
        }, timeout);
    };

    useEffect(() => {
        const showSubscribtion = eventStore.SHOW.subscribe(value => {
            if (value) {
                resetPlayerTimeout();
            }
        });

        return () => showSubscribtion.unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let canPlaySubscribtion: Subscription | null = null;
        let videoEndedSubscribtion: Subscription | null = null;

        if (videoRef.current) {
            if (videoRef.current.readyState === VideoPlayerStates.HAVE_NOTHING) {
                videoRef.current.load();
            }

            canPlaySubscribtion = fromEvent(videoRef.current, 'canplaythrough').subscribe(() => {
                resetPlayerTimeout();
            });

            videoEndedSubscribtion = fromEvent(videoRef.current, 'ended').subscribe(() => {
                if (videoRef.current) {
                    videoRef.current.style.display = 'none';
                }
                resetPlayerTimeout();
            });
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            if (canPlaySubscribtion) {
                canPlaySubscribtion.unsubscribe();
            }
            if (videoEndedSubscribtion) {
                videoEndedSubscribtion.unsubscribe();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoRef]);

    return (
        <video
            playsInline
            style={{ display: 'none' }}
            src={src}
            disablePictureInPicture
            disableRemotePlayback
            ref={videoRef}
            preload="none"
            className="egt_lobby_game_animated_banner"
        />
    );
};

interface IAnimatedBannerProps {
    src: string;
}

export default AnimatedBanner;
